import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { atlasWs } from "../api/ws";
import { useAuthStore } from "./authStore";
import { refreshSession, refreshSessionIfStale, type SessionRefreshOutcome } from "./sessionRefresh";

/** Retry cadence while the auth server is unreachable; the current token is kept meanwhile. */
const UNAVAILABLE_RETRY_MS = 15_000;

export function AuthGate() {
  const location = useLocation();
  const accessToken = useAuthStore((s) => s.accessToken);
  const accessExpiresAt = useAuthStore((s) => s.accessExpiresAt);
  const clearSession = useAuthStore((s) => s.clearSession);

  const [hydrated, setHydrated] = useState(() => useAuthStore.persist.hasHydrated());
  /** After persist rehydrates, recover access JWT from Redis refresh cookie (stored in localStorage) before routing. */
  const [resumeComplete, setResumeComplete] = useState(false);

  useEffect(() => {
    const unsub = useAuthStore.persist.onFinishHydration(() => setHydrated(true));
    void useAuthStore.persist.rehydrate();
    return unsub;
  }, []);

  useEffect(() => {
    if (!hydrated) return;

    const { accessToken: at } = useAuthStore.getState();

    let cancelled = false;

    void (async () => {
      if (at) {
        // Already have an access token in memory (same-tab navigation).
        atlasWs.connect();
        if (!cancelled) setResumeComplete(true);
        return;
      }
      // No access token — try to obtain one via the httpOnly refresh cookie.
      const outcome = await refreshSession();
      if (cancelled) return;
      if (outcome === "ok") atlasWs.connect();
      else clearSession();
      if (!cancelled) setResumeComplete(true);
    })();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated, clearSession]);

  // Same model as forge-web: the access token is short (300 s, clamped by
  // pks-bff) and the 7-day refresh cookie is the session. Renew ahead of
  // expiry, and only drop to the login page when the auth server rejects the
  // refresh credential, never because a background tab's timer ran late or
  // the network blipped.
  const [retryTick, setRetryTick] = useState(0);

  const handleOutcome = useCallback(
    (outcome: SessionRefreshOutcome) => {
      if (outcome === "rejected") clearSession();
      else if (outcome === "unavailable") setRetryTick((n) => n + 1);
      else atlasWs.reconnectIfNeeded();
    },
    [clearSession],
  );

  const refreshDelayMs = useMemo(() => {
    if (!accessExpiresAt) return null;
    if (retryTick > 0 && accessExpiresAt * 1000 - Date.now() < 90_000) return UNAVAILABLE_RETRY_MS;
    const now = Math.floor(Date.now() / 1000);
    return Math.max((accessExpiresAt - 60 - now) * 1000, 5_000);
  }, [accessExpiresAt, retryTick]);

  useEffect(() => {
    if (refreshDelayMs == null || !resumeComplete) return;
    const t = window.setTimeout(() => {
      void refreshSession().then((outcome) => {
        // Re-open the socket on the fresh token, as before.
        if (outcome === "ok") atlasWs.connect();
        else handleOutcome(outcome);
      });
    }, refreshDelayMs);
    return () => window.clearTimeout(t);
  }, [refreshDelayMs, resumeComplete, handleOutcome]);

  // Background tabs throttle or freeze timers, so the expiry timer above can
  // fire minutes late. Catch up as soon as the tab is looked at again.
  useEffect(() => {
    if (!resumeComplete) return;
    const wake = () => {
      if (document.visibilityState !== "visible") return;
      if (!useAuthStore.getState().accessToken) return;
      void refreshSessionIfStale().then(handleOutcome);
    };
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("focus", wake);
    window.addEventListener("online", wake);
    return () => {
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("focus", wake);
      window.removeEventListener("online", wake);
    };
  }, [resumeComplete, handleOutcome]);

  if (!hydrated || !resumeComplete) {
    return (
      <div className="flex h-screen items-center justify-center bg-bg text-muted font-mono text-sm">
        Loading session…
      </div>
    );
  }

  if (!accessToken) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <Outlet />;
}
